import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { RowModel } from "@tanstack/react-table";

import usePartMutations from "../../../data/usePartMutations";
import useWarehouseLocations from "../../../data/useWarehouseLocations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import Modal from "../../../layouts/Modal";
import { IWarehouseLocation } from "../../../models/Location";
import { IPart } from "../../../models/Part";
import useNotification from "../../notifications/useNotifications";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import ComboBoxInput from "../../shared/Inputs/ComboBoxInput";
import WarehouseLocationCapacity from "../../warehouseLocations/WarehouseLocationCapacity";

// The "clear the location" choice is modelled as an option with an empty id so it sits in the
// same picker as the real locations, matching WarehouseLocationDropdown on a single part.
const NO_LOCATION_ID = "";

type LocationOption = Pick<IWarehouseLocation, "id" | "displayName"> & Partial<IWarehouseLocation>;

export default function ChangeWarehouseLocationModal({ open, setOpen, setToUpdate, parts }:
  {
    open: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setToUpdate: React.Dispatch<React.SetStateAction<RowModel<IPart> | undefined>>,
    parts: IPart[]
  }) {
  const { t } = useTranslation("stock");
  const { addNotification } = useNotification();
  const { warehouseLocations } = useWarehouseLocations();
  const { bulkPatchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Part");
  const [locationUpdate, setLocationUpdate] = useState<LocationOption | undefined>(undefined);
  // ComboBoxInput owns its selection internally, so it is remounted each time the modal opens —
  // otherwise a location picked for one batch of parts stays selected for the next one.
  const [pickerKey, setPickerKey] = useState(0);

  const locationOptions = useMemo<LocationOption[]>(() => [
    { id: NO_LOCATION_ID, displayName: t("No Location") },
    ...(warehouseLocations.data?.data ?? []),
  ], [warehouseLocations.data?.data, t]);

  useEffect(() => {
    if (!open) return;
    setLocationUpdate(undefined);
    setPickerKey((key) => key + 1);
  }, [open]);

  const clearSelection = () => {
    setLocationUpdate(undefined);
    setPickerKey((key) => key + 1);
  };

  const handleChangeLocation = () => {
    if (!locationUpdate) {
      addNotification({
        primaryText: t("Choose a warehouse location first"),
        variant: "warn",
      });
      return;
    }

    const partIds = (parts ?? []).map((part) => part.id).filter(Boolean);

    if (partIds.length === 0) {
      addNotification({
        primaryText: t("No parts selected"),
        variant: "warn",
      });
      return;
    }

    bulkPatchPart.mutate({
      partIds,
      data: {
        warehouseLocationId: locationUpdate.id || null,
        // Without this flag the API treats a missing/null id as "not supplied" and leaves the
        // location alone, so clearing a location would silently do nothing.
        updateWarehouseLocation: true,
      },
    }, {
      onSuccess: (res) => {
        // A partial run still comes back 200 — parts the API skipped are only in Failed, so
        // reporting a flat success here would tell the user parts moved when they didn't.
        const failed = res?.data?.failed ?? [];

        if (failed.length > 0) {
          addNotification({
            variant: "warn",
            primaryText: t("{{count}} part(s) could not be moved", { count: failed.length }),
            secondaryText: Array.from(new Set(failed.map((failure) => failure.reason))).join(", "),
          });
        } else {
          saveHandlers.onSuccess();
        }

        setToUpdate(undefined);
        setLocationUpdate(undefined);
        setOpen(false);
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  const selectedLocation = locationUpdate?.id
    ? (warehouseLocations.data?.data ?? []).find((location) => location.id === locationUpdate.id)
    : undefined;

  return (
    <Modal open={open} width={"max-w-3xl"} setOpen={setOpen}>
      <div className="space-y-4">
        <p>{t("You are about to update {{count}} parts!", { count: parts?.length })}</p>
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-2">

          <div className="flex items-center w-full gap-x-2">
            <ComboBoxInput
              key={pickerKey}
              label=""
              register={null}
              registerName={"warehouseLocation"}
              options={locationOptions}
              setValue={(_registerName: string, value: LocationOption | undefined) => setLocationUpdate(value)}
              searchField={"displayName"}
              disabled={warehouseLocations.isLoading || bulkPatchPart.isLoading}
              containerClasses="w-full"
              placeholder={t("Start Typing to Search Warehouse Locations")}
            />
            <XMarkIcon className="h-5 w-5 text-red-600 cursor-pointer" onClick={clearSelection} />
          </div>

          <div className="w-full mt-1">
            <ButtonInput
              label={t("Update All")}
              classes={"w-full "}
              onClick={handleChangeLocation}
              isLoading={bulkPatchPart.isLoading}
              disabled={!locationUpdate}
            />
          </div>
        </div>

        {locationUpdate && (
          <p className="text-sm text-slate-500">
            {locationUpdate.id
              ? <span className="font-medium text-slate-700">{t("Moving to {{location}}", { location: locationUpdate.displayName })}</span>
              : t("The warehouse location will be cleared on these parts.")}
            {selectedLocation && (
              <span className="ml-2">
                {t("Capacity")}: <WarehouseLocationCapacity location={selectedLocation} />
              </span>
            )}
          </p>
        )}
      </div>
    </Modal>
  )
};
